/**
 * ProtectedRoute Component
 * Guards routes that require authentication
 */

import { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../stores/auth.store';
import { PageLoader } from '../shared';

interface ProtectedRouteProps {
  children: React.ReactNode;
  redirectTo?: string;
}

/**
 * ProtectedRoute component that checks authentication before rendering children
 * 
 * If user is not authenticated, redirects to plz-admin for login
 */
export function ProtectedRoute({ children, redirectTo }: ProtectedRouteProps) {
  const location = useLocation();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isLoading = useAuthStore((state) => state.isLoading);

  // Handle redirect to plz-admin when not authenticated
  useEffect(() => {
    if (!isLoading && !isAuthenticated && !redirectTo) {
      const plzAdminUrl = import.meta.env.VITE_PLZ_ADMIN_URL || 'https://partner-portal.test.plz.pl';
      const currentUrl = window.location.href;
      
      // Redirect to plz-admin with return URL
      window.location.href = `${plzAdminUrl}?returnUrl=${encodeURIComponent(currentUrl)}`;
    }
  }, [isLoading, isAuthenticated, redirectTo]);

  // Show loader while auth state is being determined
  if (isLoading) {
    return <PageLoader />;
  }

  // Redirect to plz-admin if not authenticated
  if (!isAuthenticated) {
    // If custom redirect provided, use it (for internal redirects)
    if (redirectTo) {
      return (
        <Navigate
          to={redirectTo}
          state={{ from: location.pathname }}
          replace
        />
      );
    }

    // Show loader while redirect happens in useEffect
    return <PageLoader />;
  }

  // Render protected content
  return <>{children}</>;
}

export default ProtectedRoute;
